import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Radio, X, ShieldAlert, PenSquare, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

/**
 * Sprint 24 — Persistent red/orange banner shown whenever the operator is
 * viewing another tenant's workspace via a support session.
 */
export default function SupportModeBanner() {
  const { support, restaurant, stopSupport, enableWriteSupport } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!support) return null;

  const targetName = restaurant?.brand_name || restaurant?.name || support.restaurant_id;
  const writeOn = !!support.write_enabled;

  const doExit = async () => {
    setBusy(true);
    try { await stopSupport(); toast.success("Exited support mode"); }
    finally { setBusy(false); }
  };
  const doEnableWrite = async () => {
    if (!window.confirm("Enable write support for 30 minutes? All changes will be audit logged.")) return;
    setBusy(true);
    try {
      const res = await enableWriteSupport();
      toast.success(`Write support enabled until ${new Date(res.write_expires_at).toLocaleTimeString()}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to enable write"); }
    finally { setBusy(false); }
  };

  return (
    <div
      className={`w-full text-white px-4 py-2 flex items-center gap-3 shadow-md sticky top-0 z-[60] ${writeOn ? "bg-red-600" : "bg-orange-600"}`}
      data-testid="support-mode-banner"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {writeOn ? <ShieldAlert className="w-4 h-4 shrink-0 animate-pulse" /> : <Radio className="w-4 h-4 shrink-0" />}
        <div className="text-sm font-semibold truncate">
          Support Mode: You are viewing <span className="font-mono">{targetName}</span>
          {writeOn && <span className="ml-2 text-[11px] uppercase tracking-widest bg-white/20 rounded px-1.5 py-0.5">WRITE ENABLED</span>}
        </div>
      </div>
      {!writeOn && (
        <Button size="sm" variant="secondary" className="h-8 rounded-md gap-1" onClick={doEnableWrite} disabled={busy} data-testid="support-enable-write">
          <PenSquare className="w-3.5 h-3.5" /> Enable write (30m)
        </Button>
      )}
      <Button size="sm" variant="secondary" className="h-8 rounded-md gap-1" onClick={doExit} disabled={busy} data-testid="support-exit">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Exit
      </Button>
    </div>
  );
}
